import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { statesOptions } from 'src/app/data/states';
import { SignupService } from 'src/app/services/signup/signup.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import { AuthService } from 'src/app/services/auth/auth.service';
import DDIOptions from 'src/app/data/ddi';
import Utils, { GetHelper, payloadsEqual, UtilsValidators } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Router } from '@angular/router';

@Component({
  selector: 'app-signup-unified-person-complementary-professional',
  templateUrl:
    './signup-unified-person-complementary-professional.component.html',
  styleUrls: [
    './signup-unified-person-complementary-professional.component.scss',
  ],
})
export class SignupUnifiedPersonComplementaryProfessionalComponent
  implements OnInit, OnDestroy
{
  DDIOptions = DDIOptions;

  form = this.formBuilder.group({
    document_number: [null, [UtilsValidators.cnpj]],
    corporate_name: [null, []],
    occupation: [null, []],
    corporate_email: [
      null,
      [Utils.validateWhenNotEmpty(UtilsValidators.email)],
    ],

    phone_ddi: ['55', []],
    phone_number: [null, [UtilsValidators.telefone]],
    branch: [null, []],

    cep: [null, [UtilsValidators.cep]],
    public_place: [null, []],
    number: [null, [Validators.max(99999)]],
    complement: [null, []],
    district: [null, []],
    city: [null, []],
    uf: [null, []],
    country: [null, []],
  });

  zipcodeFetched = false;

  addressObj: AddressProps = {
    public_place: '',
    district: '',
    city: '',
    uf: '',
    country: '',
  };

  states = statesOptions;

  isForeign = false;

  data: ProfessionalResponse;

  loading = false;

  event: any;

  foundPerson = false;

  loadingContent = false;

  userRole: string;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private utilsService: UtilsService,
    private maskPipe: MaskPipe,
    private authService: AuthService,
    private api: ApiService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    // Detectar se é shareholder baseado na URL
    this.userRole = this.router.url.includes('shareholder')
      ? 'shareholder'
      : 'other';

    this.getDataAndFillForm();

    this.event = this.signupService.sendPersonData.subscribe((value) => {
      if (value === 'complementaryProfessional') {
        this.sendData();
      }
    });

    this.setForeign();
    this.setShareholderValidations();

    // Atualiza o status inicial
    this.updateFormStatus();

    // Atualiza o status quando o formulário mudar
    this.form.valueChanges.subscribe(() => {
      this.updateFormStatus();
    });

    this.form.statusChanges.subscribe(() => {
      this.updateFormStatus();
    });

    this.form.controls.document_number.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged((a: any, b: any) => a === b)
      )
      .subscribe(async (value) => {
        if (!value) {
          this.form.controls.document_number.setValue(null);
          if (this.foundPerson) {
            this.foundPerson = false;
            this.form.controls.corporate_name.setValue('');
          }
        } else {
          if (value.length === 18) {
            const { name, foundPerson } = await this.api.getPersonName(value);

            this.foundPerson = foundPerson;

            if (name) this.form.controls.corporate_name.setValue(name);
          }
        }
      });

    this.form.controls.cep.valueChanges.subscribe((zipcode) => {
      if (zipcode.length === 9) {
        // Para shareholders, os campos de endereço já são obrigatórios
        if (this.userRole !== 'shareholder') {
          this.form.controls.public_place.setValidators([Validators.required]);
          this.form.controls.number.setValidators([Validators.required]);
          this.form.controls.district.setValidators([Validators.required]);
          this.form.controls.city.setValidators([Validators.required]);
          this.form.controls.uf.setValidators([Validators.required]);
          this.form.controls.country.setValidators([Validators.required]);
        }
      } else {
        // Para shareholders, manter obrigatoriedade mesmo sem CEP
        if (this.userRole !== 'shareholder') {
          this.form.controls.public_place.setValidators([]);
          this.form.controls.number.setValidators([]);
          this.form.controls.district.setValidators([]);
          this.form.controls.city.setValidators([]);
          this.form.controls.uf.setValidators([]);
          this.form.controls.country.setValidators([]);
        }
      }

      if (zipcode.length === 0) {
        this.form.controls.public_place.setValue(null);
        this.form.controls.number.setValue(null);
        this.form.controls.district.setValue(null);
        this.form.controls.city.setValue(null);
        this.form.controls.uf.setValue(null);
        this.form.controls.country.setValue('1');
      }

      this.form.controls.public_place.updateValueAndValidity();
      this.form.controls.number.updateValueAndValidity();
      this.form.controls.district.updateValueAndValidity();
      this.form.controls.city.updateValueAndValidity();
      this.form.controls.uf.updateValueAndValidity();
      this.form.controls.country.updateValueAndValidity();
    });

    this.signupService.fillFormEvent.subscribe(() => {
      let payload: any = {
        document_number: this.maskPipe.transform(
          fakerbr.br.cnpj(),
          '00.000.000/0000-00'
        ),
        corporate_name: fakerbr.company.companyName(),
        occupation: fakerbr.name.jobDescriptor(),
        corporate_email: fakerbr.internet.email(),

        phone_ddi: '55',
        phone_number: this.maskPipe.transform(
          '139' +
            String(fakerbr.random.number({ min: 11111111, max: 99999999 })),
          '(00) 00000-0000'
        ),
        branch: null,
      };

      if (!this.zipcodeFetched) {
        const zipcode = fakerbr.address.zipCodeValidByState('SP');

        const formatted = this.maskPipe.transform(zipcode, '00000-000');

        payload = {
          ...payload,
          cep: formatted,
          number: fakerbr.random.number({ min: 1, max: 1000 }),
          complement: 'Casa',
          country: '23',
        };

        this.handleSearchZipcode({
          target: { value: formatted },
        });

        this.zipcodeFetched = true;
      }

      this.form.patchValue(payload);
    });
  }

  /**
   * Obtém o register_uuid da URL (obrigatório, sem fallback)
   */
  private getRegisterUuid(): string | null {
    return this.route.snapshot.paramMap.get('id');
  }

  /**
   * Obtém os params com register_uuid para as chamadas da API
   */
  private getRegisterParams(): any {
    const routeId = this.getRegisterUuid();

    if (!routeId) {
      this.toast.show('error', 'Erro', 'Register UUID não encontrado na rota');
      return null;
    }

    return { register_uuid: routeId };
  }

  setForeign() {
    if (this.authService.user?.nationality?.country !== 'Brasil') {
      this.isForeign = true;
    }
  }

  setShareholderValidations() {
    if (this.userRole === 'shareholder') {
      // Campos obrigatórios para shareholder
      this.form.controls.document_number.setValidators([
        Validators.required,
        UtilsValidators.cnpj,
      ]);
      this.form.controls.corporate_name.setValidators([Validators.required]);
      this.form.controls.occupation.setValidators([Validators.required]);
      this.form.controls.corporate_email.setValidators([
        Validators.required,
        UtilsValidators.email,
      ]);
      this.form.controls.phone_number.setValidators([
        Validators.required,
        UtilsValidators.telefone,
      ]);
      this.form.controls.cep.setValidators([
        Validators.required,
        UtilsValidators.cep,
      ]);
      this.form.controls.public_place.setValidators([Validators.required]);
      this.form.controls.number.setValidators([Validators.required]);
      this.form.controls.district.setValidators([Validators.required]);
      this.form.controls.city.setValidators([Validators.required]);
      this.form.controls.uf.setValidators([Validators.required]);
      this.form.controls.country.setValidators([Validators.required]);

      // Ramal e complemento permanecem opcionais
      this.form.controls.branch.setValidators([]);
      this.form.controls.complement.setValidators([]);

      // Atualizar validações
      this.form.controls.document_number.updateValueAndValidity();
      this.form.controls.corporate_name.updateValueAndValidity();
      this.form.controls.occupation.updateValueAndValidity();
      this.form.controls.corporate_email.updateValueAndValidity();
      this.form.controls.phone_number.updateValueAndValidity();
      this.form.controls.cep.updateValueAndValidity();
      this.form.controls.public_place.updateValueAndValidity();
      this.form.controls.number.updateValueAndValidity();
      this.form.controls.district.updateValueAndValidity();
      this.form.controls.city.updateValueAndValidity();
      this.form.controls.uf.updateValueAndValidity();
      this.form.controls.country.updateValueAndValidity();
      this.form.controls.branch.updateValueAndValidity();
      this.form.controls.complement.updateValueAndValidity();

      // Atualiza o status após configurar as validações
      this.updateFormStatus();
    }
  }

  /**
   * Atualiza o status do formulário verificando se todos os campos obrigatórios estão válidos
   */
  updateFormStatus() {
    if (this.userRole === 'shareholder') {
      // Para shareholder, verifica todos os campos obrigatórios
      const requiredFields = [
        'document_number',
        'corporate_name',
        'occupation',
        'corporate_email',
        'phone_number',
        'cep',
        'public_place',
        'number',
        'district',
        'city',
        'uf',
        'country',
      ];

      const allFieldsValid = requiredFields.every((fieldName) => {
        const control = this.form.get(fieldName);
        return control && control.valid && control.value;
      });

      this.signupService.setPersonFormStatus(
        'complementaryProfessional',
        allFieldsValid
      );
    } else {
      // Para outros roles, usa a validação padrão do formulário
      this.signupService.setPersonFormStatus(
        'complementaryProfessional',
        this.form.status === 'VALID'
      );
    }
  }

  async handleSearchZipcode(event: any) {
    this.loading = true;
    const zipcode = event.target.value;

    var obj = {
      public_place: '',
      district: '',
      city: '',
      uf: '',
      country: '',
    };

    try {
      const zipcodeRes = await this.utilsService.getZipcode(zipcode);

      if (zipcodeRes) {
        const foundCep = Object.keys(zipcodeRes).some((key) => {
          return key === 'partner_response' ? false : zipcodeRes[key] !== '';
        });

        if (foundCep) {
          obj = {
            public_place: zipcodeRes.logradouro,
            district: zipcodeRes.bairro,
            city: zipcodeRes.localidade,
            uf: zipcodeRes.uf,
            country: '23',
          };

          this.form.patchValue(obj);

          this.zipcodeFetched = true;
        } else {
          Utils.resetZipcodeFields(this.form);
          this.form.updateValueAndValidity();

          this.zipcodeFetched = zipcode.length === 9;
        }
      }
    } catch (error) {
      this.zipcodeFetched = false;
      console.error(error);
    }
    this.addressObj = obj;

    this.loading = false;
    this.updateFormStatus(); // Atualiza status após buscar CEP
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    const payload: Record<string, any> = {
      document_number: Utils.onlyNumbers(valuesHelper.get('document_number')),
      corporate_name: valuesHelper.get('corporate_name'),
      occupation: valuesHelper.get('occupation'),
      corporate_email: valuesHelper.get('corporate_email'),
    };

    if (valuesHelper.get('cep')) {
      payload['address'] = {
        cep: valuesHelper.get('cep'),
        public_place: valuesHelper.get('public_place'),
        number: valuesHelper.get('number') || null,
        country: Number(valuesHelper.get('country')),
        complement: valuesHelper.get('complement'),
        district: valuesHelper.get('district'),
        city: valuesHelper.get('city'),
        uf: valuesHelper.get('uf'),
        type: 1,
        address_type: 'professional',
      };
    }

    if (valuesHelper.get('cep')) {
      payload['phone'] = {
        type: 'phone',
        code: Number(valuesHelper.get('phone_ddi')),
        number: Utils.onlyNumbers(valuesHelper.get('phone_number')),
        branch: valuesHelper.get('branch') ?? null,
      };
    }

    return payload;
  }

  async sendData() {
    const params = this.getRegisterParams();
    if (!params) return;

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      this.signupService.setPersonStep('complementaryBond');
      this.signupService.changePersonStepEvent.emit('bond');
      return;
    }

    this.signupService.loading = true;

    try {
      const professionalId = (this.data as any)?.id;

      if (this.data && professionalId) {
        await this.api.put<ApiResponse<ProfessionalResponse>>({
          route: `api/registration/professional-data/${professionalId}/`,
          params: params,
          token: true,
          body: payload,
        });
      } else {
        const response = await this.api.post<ApiResponse<ProfessionalResponse>>(
          {
            route: 'api/registration/professional-data/',
            params: params,
            token: true,
            body: payload,
          }
        );
        this.data = response.data;
      }

      this.initialPayload = this.buildPayloadFromForm();
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');

      this.signupService.setPersonStep('complementaryBond');
      this.signupService.changePersonStepEvent.emit('bond');
    } catch (error) {
      this.toast.show('error', 'Erro', error?.error?.message || 'Erro ao salvar os dados');
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }

  async getDataAndFillForm() {
    this.loadingContent = true;
    try {
      const params = this.getRegisterParams();

      const { data } = await this.api.get<ApiResponse<ProfessionalResponse>>({
        route: 'api/registration/professional-data/',
        params: params,
        token: true,
      });

      this.data = data;

      // Atualiza o status do formulário após carregar os dados
      this.updateFormStatus();

      this.addressObj = {
        public_place: data.address?.public_place,
        district: data.address?.district,
        city: data.address?.city,
        uf: data.address?.uf,
        country: String(data.address?.country),
      };

      this.form.patchValue({
        ...data,
        ...data.address,
        document_number: this.maskPipe.transform(
          data.document_number,
          '00.000.000/0000-00'
        ),
        phone_ddi: data.phone ? String(data.phone.code) : '55',
        phone_number: data.phone
          ? this.maskPipe.transform(data.phone.number, '(00) 00000-0000')
          : null,
        branch: data.phone ? data.phone.branch : null,
      });

      if (data.address) {
        this.form.patchValue({
          country: data.address.country?.toString(),
        });
        this.zipcodeFetched = true;
      }

      this.initialPayload = this.buildPayloadFromForm();
    } catch (error) {
      if (error.status !== 404) {
        // this.toast.show('error', 'Erro', error.error.message);
      }
      this.initialPayload = null;
    } finally {
      this.loadingContent = false;
    }
  }
}
